import { TUILogin } from '@tencentcloud/tui-core-lite'
import { obtainUsersig } from '@/api/login'
import store from '@/store'
import { ensureTUIKitReady } from '@/utils/tUIKitInit'

const FIXED_SDK_APP_ID = 1600138619
const FRAMEWORK = 'vue2'
let loginPromise = null
const LOGIN_READY_RETRY_TIMES = 30
const LOGIN_READY_RETRY_INTERVAL = 100

function isDuplicateLoginError(error) {
  const code = error && (error.code || error?.data?.code || error?.response?.data?.code)
  const message = String(
    (error && (error.message || error?.data?.message || error?.response?.data?.message)) || ''
  )
  return Number(code) === 2025 || message.includes('重复登录')
}

function getUserIDFromLoginInfo() {
  const stateUser = store && store.getters ? store.getters['user/userInfo'] : null
  const localUser = JSON.parse(localStorage.getItem('userInfo') || 'null')
  const userInfo = stateUser || localUser || {}
  const rawUserID = userInfo.userID || userInfo.userId || userInfo.id || userInfo.uid
  return rawUserID ? String(rawUserID) : ''
}

function getLiteLoginContext() {
  try {
    if (typeof TUILogin.getContext !== 'function') return null
    return TUILogin.getContext() || null
  } catch (_) {
    return null
  }
}

function isLiteContextReady(userID) {
  const ctx = getLiteLoginContext()
  if (!ctx) return false
  const ctxUser = String(ctx.userID || '').trim()
  const expect = String(userID || '').trim()
  if (!ctxUser || !expect || ctxUser !== expect) return false
  const chat = ctx.chat || ctx.tim
  return !!chat
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function isLiteSDKReady(userID) {
  const ctx = getLiteLoginContext()
  if (!ctx) return false
  const chat = ctx.chat || ctx.tim
  if (!chat) return false
  const expect = String(userID || '').trim()
  const ctxUser = String(ctx.userID || '').trim()
  if (!expect || !ctxUser || expect !== ctxUser) return false

  try {
    if (typeof chat.getLoginUser === 'function') {
      const loginUser = await chat.getLoginUser()
      if (loginUser && String(loginUser).trim() !== expect) return false
    }
  } catch (_) {
    return false
  }

  try {
    if (typeof chat.isReady === 'function' && chat.isReady() === false) {
      return false
    }
  } catch (_) {
    return false
  }

  // 通过轻量 API 探测连接可用性，避免仅对象存在但链路不可用
  try {
    if (typeof chat.getMyProfile === 'function') {
      await chat.getMyProfile()
      return true
    }
    if (typeof chat.getConversationList === 'function') {
      await chat.getConversationList({ count: 1, nextReqMessageID: '' })
      return true
    }
  } catch (_) {
    return false
  }
  return true
}

/** 从登录接口返回的 userVo（或 store 中的 userInfo）读取 IM userSig */
function getUserSigFromUserInfo(userInfo) {
  if (!userInfo || typeof userInfo !== 'object') return ''
  const sig = userInfo.userSig != null ? userInfo.userSig : userInfo.user_sig
  if (sig == null || String(sig).trim() === '') return ''
  return String(sig)
}

/**
 * 登录 TUIKit：优先使用 userVo.userSig（登录/注册写入 store 后已有），否则再请求 obtainUsersig。
 * 业务侧应在：注册/登录/微信授权成功、绑定手机写入 userInfo 后调用，或由 restoreIMSessionOnStartup 在应用启动时恢复。
 *
 * @param {{ userSig?: string, userID?: string, userInfo?: object, forceRelogin?: boolean }} [options] 可选覆盖（一般不传，读 store）
 */
export async function loginIM(options = {}) {
  if (loginPromise) return loginPromise
  loginPromise = (async () => {
  await ensureTUIKitReady()
  let userSig =
    options.userSig != null && String(options.userSig).trim() !== ''
      ? String(options.userSig)
      : ''
  if (!userSig) {
    const ui =
      options.userInfo ||
      (store.getters && store.getters['user/userInfo']) ||
      JSON.parse(localStorage.getItem('userInfo') || 'null')
    userSig = getUserSigFromUserInfo(ui)
  }
  if (!userSig) {
    let res
    try {
      res = await obtainUsersig({})
    } catch (e) {
      const status = e.response && e.response.status
      const data = e.response && e.response.data
      if (status === 500) {
        console.warn('[IM] obtainUsersig 失败:', status, data && (data.message || data.msg || data))
        throw new Error(
          'IM 服务暂时不可用（服务器异常）。聊天相关功能可能无法使用，请稍后重试；若持续出现请联系客服。'
        )
      }
      const msg =
        (data && (data.message || data.msg || data.error)) || e.message || '获取 IM 签名失败'
      throw new Error(typeof msg === 'string' ? msg : '获取 IM 签名失败')
    }
    userSig = String((res && res.data && res.data.userSig) || '')
  }
  const userID =
    options.userID != null && String(options.userID).trim() !== ''
      ? String(options.userID)
      : getUserIDFromLoginInfo()
  if (!userID || !userSig) {
    throw new Error('IM鉴权参数缺失：请检查 userID 或 userSig')
  }
  const auth = { SDKAppID: FIXED_SDK_APP_ID, userID, userSig }
  const forceRelogin = !!options.forceRelogin
  if (!forceRelogin && isLiteContextReady(userID) && await isLiteSDKReady(userID)) {
    return auth
  }
  if (forceRelogin) {
    try { await TUILogin.logout() } catch (_) {}
  }
  // 不在这里做“上下文相同即跳过”的短路。
  // 某些场景（如网络抖动后）context 还在，但底层 IM/Call 引擎已掉线，
  // 会导致发起通话时报 User not logged in。
  try {
    await TUILogin.login({
      SDKAppID: auth.SDKAppID,
      userID: auth.userID,
      userSig: auth.userSig,
      useUploadPlugin: true,
      framework: FRAMEWORK
    })
  } catch (error) {
    if (!isDuplicateLoginError(error)) {
      throw error
    }
  }
  let liteReady = false
  for (let i = 0; i < LOGIN_READY_RETRY_TIMES; i++) {
    // eslint-disable-next-line no-await-in-loop
    liteReady = isLiteContextReady(userID) && await isLiteSDKReady(userID)
    if (liteReady) break
    // eslint-disable-next-line no-await-in-loop
    await sleep(LOGIN_READY_RETRY_INTERVAL)
  }
  if (!liteReady) {
    throw new Error('IM 登录未就绪，请稍后重试')
  }
  return auth
  })()
  try {
    return await loginPromise
  } finally {
    loginPromise = null
  }
}

/**
 * 应用启动时恢复 IM：本地已登录且 store / localStorage 具备 userID 与 userSig（或可由 obtainUsersig 补齐）时执行 loginIM。
 * 未登录或缺少凭据时静默返回，不抛错（避免首屏无 token 时报错）。
 */
export async function restoreIMSessionOnStartup() {
  const isLogin = store.getters && store.getters['user/isLogin']
  if (!isLogin) return
  try {
    await loginIM()
  } catch (e) {
    console.warn('[IM] restore session on startup failed:', (e && e.message) || e)
  }
}

export async function logoutIM() {
  try {
    await TUILogin.logout()
  } catch (error) {
    // 忽略未登录状态的下线异常，避免影响主流程
  }
}
